import { CanUpdateErrorState, ErrorStateMatcher, mixinErrorState } from '@angular/material/core';
import { MatFormFieldControl } from '@angular/material/form-field';
import { ControlValueAccessor, UntypedFormControl, FormGroupDirective, NgControl, NgForm } from '@angular/forms';
import { FocusMonitor } from '@angular/cdk/a11y';
import { coerceBooleanProperty } from '@angular/cdk/coercion';
import {
  Component,
  DoCheck,
  ElementRef,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Optional,
  Self,
  ViewEncapsulation,
} from '@angular/core';

import { distinctUntilChanged, filter, takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';

class MatInputBase {
  constructor(
    public _defaultErrorStateMatcher: ErrorStateMatcher,
    public _parentForm: NgForm,
    public _parentFormGroup: FormGroupDirective,
    /** @docs-private */
    public ngControl: NgControl,
    public stateChanges: Subject<void>,
  ) {}
}

export const _MatInputMixinBase = mixinErrorState(MatInputBase);

@DecorateUntilDestroy()
@Component({
  selector: 'crm-ngx-wig-mat-input',
  template: `
    <ngx-wig [formControl]="editorControl" [placeholder]="placeholder" [buttons]="'edithtml'"></ngx-wig>
  `,
  styleUrls: ['./ngx-wig-mat-input.component.scss'],
  encapsulation: ViewEncapsulation.None,
  providers: [
    {
      provide: MatFormFieldControl,
      useExisting: NgxWigMatInputComponent,
    },
  ],
})
export class NgxWigMatInputComponent
  extends _MatInputMixinBase
  implements
    CanUpdateErrorState,
    ControlValueAccessor,
    MatFormFieldControl<string>,
    OnChanges,
    OnInit,
    DoCheck,
    OnDestroy
{
  static nextId = 0;

  @HostBinding() id = `crm-ngx-wig-mat-input-${NgxWigMatInputComponent.nextId++}`;

  @HostBinding('attr.aria-describedby') describedBy = '';

  get value(): string {
    return this.editorControl.value;
  }

  set value(newValue: string) {
    this.writeValue(newValue);
  }

  @Input()
  get placeholder(): string {
    return this._placeholder;
  }

  set placeholder(newPlaceholder: string) {
    this._placeholder = newPlaceholder;
    this.stateChanges.next();
  }

  private _placeholder: string;

  focused = false;

  get empty(): boolean {
    return !!this.editorControl.value;
  }

  @Input()
  get required(): boolean {
    return this._required;
  }

  set required(isRequired: boolean) {
    this._required = coerceBooleanProperty(isRequired);
    this.stateChanges.next();
  }

  private _required = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }

  set disabled(isDisabled: boolean) {
    this.setDisabledState(isDisabled);
  }

  private _disabled = false;

  shouldPlaceholderFloat = false;

  shouldLabelFloat = false;

  readonly controlType: string = 'crm-ngx-wig';

  private _destroy$$: Subject<void> = new Subject<void>();

  editorControl: UntypedFormControl = new UntypedFormControl();

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    @Optional() _parentForm: NgForm,
    @Optional() _parentFormGroup: FormGroupDirective,
    _defaultErrorStateMatcher: ErrorStateMatcher,
    private _focusMonitor: FocusMonitor,
    private _elRef: ElementRef,
  ) {
    super(_defaultErrorStateMatcher, _parentForm, _parentFormGroup, ngControl, new Subject<void>());

    if (ngControl !== null) {
      this.ngControl.valueAccessor = this;
    }
  }

  ngOnInit(): void {
    this._focusMonitor
      .monitor(this._elRef.nativeElement, true)
      .pipe(takeUntilDestroyed(this))
      .subscribe((origin: string) => {
        this.focused = !!origin;

        if (!this.focused) {
          this._onTouched();
        }

        this.stateChanges.next();
      });

    this.editorControl.valueChanges
      .pipe(
        distinctUntilChanged(),
        filter(() => this.editorControl.enabled),
        takeUntil(this._destroy$$),
      )
      .subscribe((newValue: string) => {
        const strippedValue = this.stripHtml(newValue);
        this._onChange(strippedValue);
      });
  }

  ngDoCheck(): void {
    if (this.ngControl) {
      this.updateErrorState();
    }
  }

  ngOnChanges(): void {
    this.stateChanges.next();
  }

  setDescribedByIds(ids: string[]): void {
    this.describedBy = ids.join(' ');
  }

  onContainerClick(_event: MouseEvent): void {}

  writeValue(newValue: string): void {
    this.editorControl.setValue(newValue, { emitEvent: false });
    this.stateChanges.next();
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabled = coerceBooleanProperty(isDisabled);

    if (this._disabled) {
      this.editorControl.disable();
    } else {
      this.editorControl.enable();
    }

    this.stateChanges.next();
  }

  ngOnDestroy(): void {
    this._destroy$$.next();
    this._destroy$$.complete();

    this.stateChanges.complete();

    this._focusMonitor.stopMonitoring(this._elRef.nativeElement);
  }

  registerOnChange(fn: (value: string) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => {}): void {
    this._onTouched = fn;
  }

  private _onChange: (value: string) => void = () => {};
  private _onTouched = () => {};

  private stripHtml(text: string): string {
    const allowedTags = ['DIV', 'BR', 'SPAN', 'P', 'A'];
    const allowedAttrs = ['href'];

    const parser = new DOMParser();
    let dom;
    try {
      dom = parser.parseFromString(text, 'text/html');
    } catch (e) {
      return text;
    }
    const content = dom.body;

    Array.from(content.getElementsByTagName('*')).forEach((tag: Element) => {
      if (!allowedTags.includes(tag.tagName)) {
        tag.replaceWith(...Array.from(tag.childNodes));
        return;
      }

      Array.from(tag.attributes).forEach(attr => {
        if (!allowedAttrs.includes(attr.name)) {
          tag.removeAttribute(attr.name);
        }
      });
    });

    return content.innerHTML;
  }
}
